import { Avatar, Box, Badge, Checkbox, Typography } from '@mui/material';
import useTheme from '@mui/material/styles/useTheme';
import { IconArrowLeft, IconArrowRight } from '@tabler/icons';

// Corta o texto da última mensagem ANTES de chegar no DOM, em vez de
// confiar só no CSS (`noWrap` + `text-overflow: ellipsis`) pra isso - um
// texto sem espaço nenhum pra quebrar (ex: um link colado, um número
// grande) faz o item do flexbox crescer além do espaço disponível MESMO
// com `noWrap`/`overflow: hidden` (um item de flex só encolhe de verdade
// com `min-width: 0`, que depende de toda a cadeia de containers acima
// estar certinha - um único container no meio sem isso já quebra o
// corte, empurrando a linha inteira pra direita e forçando scroll
// horizontal na tela toda). Cortando a STRING em si, o problema não tem
// como acontecer, não importa o que aconteça com o CSS ao redor.
const MAX_CHARS_ULTIMA_MENSAGEM = 60;
function truncarUltimaMensagem(texto) {
    if (!texto || texto.length <= MAX_CHARS_ULTIMA_MENSAGEM) return texto;
    return `${texto.slice(0, MAX_CHARS_ULTIMA_MENSAGEM).trimEnd()}...`;
}

// Mesmo problema, mesmo motivo (ver comentário acima) - um nome de
// contato bem comprido (import de planilha bagunçada, usuário de
// Instagram longo) sem nenhum espaço pra quebrar.
const MAX_CHARS_NOME_CONTATO = 32;
function truncarNomeContato(texto) {
    if (!texto || texto.length <= MAX_CHARS_NOME_CONTATO) return texto;
    return `${texto.slice(0, MAX_CHARS_NOME_CONTATO).trimEnd()}...`;
}

function stringToColor(string) {
    let hash = 0;
    let i;

    /* eslint-disable no-bitwise */
    for (i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';

    for (i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }
    /* eslint-enable no-bitwise */

    return color;
}

function stringAvatar(name) {
    if (!name.includes(' ')) {
        return {
            sx: {
                bgcolor: stringToColor(name),
                color: '#FFFFFF'
            },
            children: `${name[0]}`
        };
    }
    return {
        sx: {
            bgcolor: stringToColor(name)
        },
        children: `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`
    };
}

function formatConversationTimestamp(isoString) {
    // Sem isso, uma conversa sem last_message_time (null/undefined vindo da
    // API) virava `new Date(null)` = época Unix (1970), que ajustada pro
    // fuso de SP sempre exibia "31/12" - independente da data real.
    if (!isoString) return '';

    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return '';

    // sempre ajusta para o fuso de São Paulo
    const options = { timeZone: 'America/Sao_Paulo' };
    const today = new Date().toLocaleDateString('pt-BR', options);
    const yesterday = new Date(Date.now() - 86400000).toLocaleDateString('pt-BR', options); // -1 dia

    const dateStr = date.toLocaleDateString('pt-BR', options);

    if (dateStr === today) {
        return date.toLocaleTimeString('pt-BR', {
            ...options,
            hour: '2-digit',
            minute: '2-digit'
        });
    } else if (dateStr === yesterday) {
        return `ontem ás ${date.toLocaleTimeString('pt-BR', {
            ...options,
            hour: '2-digit',
            minute: '2-digit'
        })}`;
    } else {
        const dataFormatada = date.toLocaleDateString('pt-BR', {
            ...options,
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
        const horaFormatada = date.toLocaleTimeString('pt-BR', {
            ...options,
            hour: '2-digit',
            minute: '2-digit'
        });
        return `${dataFormatada} ${horaFormatada}`;
    }
}

const CHANNEL_LABELS = {
    whatsapp: { icon: '/whatsapp_icon.png', label: 'WhatsApp' },
    messenger: { icon: '/messenger_icon.png', label: 'Messenger' },
    chat: { icon: '/chat_icon3.png', label: 'Chat Web' },
    instagram: { icon: '/instagram_icon.png', label: 'Instagram' },
    email: { icon: '/email_icon.png', label: 'E-mail' }
};

// Mesmos rótulos das abas de `Chats3.js` (No Bot/Novas/Abertas/Em
// andamento/Pendente/Fechada) - usado só quando `showState` (busca que
// junta as 6 etapas numa lista só, ver `Chats3.js`) precisa deixar claro
// em qual etapa cada resultado está, já que fora dessa lista combinada a
// aba em si já deixa isso óbvio.
const STATE_LABELS = {
    in_bot: 'No Bot',
    new: 'Novas',
    open: 'Abertas',
    in_progress: 'Em andamento',
    pending: 'Pendente',
    closed: 'Fechada'
};

const ConversasGeral = ({ conversas, onOpenConversation, showState = false, selectionMode = false, selectedIds = null, onToggleSelect }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const channel = CHANNEL_LABELS;

    return (
        <Box
            sx={{
                height: 'calc(100vh - 320px)',
                minHeight: 240,
                overflowY: 'auto',
                overflowX: 'hidden',
                pr: 0.5,
                '&::-webkit-scrollbar': {
                    width: '8px'
                },
                '&::-webkit-scrollbar-thumb': {
                    background: isDark ? 'rgba(110,220,255,0.18)' : 'rgba(0,120,220,0.20)',
                    borderRadius: '999px'
                }
            }}
        >
            {conversas.map((conversation) => {
                const naoLida = conversation.read === false && conversation.type_author_last_message === 'user';
                const isSelected = selectionMode && selectedIds?.has(conversation.id);

                return (
                    <Box
                        key={conversation.id}
                        onClick={() => (selectionMode ? onToggleSelect?.(conversation.id) : onOpenConversation(conversation))}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            borderRadius: '22px',
                            cursor: 'pointer',
                            mb: 1.2,
                            p: 1.5,
                            border: isSelected
                                ? isDark
                                    ? '1px solid rgba(140,240,255,0.7)'
                                    : '1px solid rgba(0,140,255,0.7)'
                                : isDark
                                ? '1px solid rgba(110,220,255,0.12)'
                                : '1px solid rgba(0,120,220,0.16)',
                            background: isSelected
                                ? isDark
                                    ? 'linear-gradient(180deg, rgba(0,120,220,0.30), rgba(0,80,160,0.30))'
                                    : 'linear-gradient(180deg, rgba(190,230,255,0.95), rgba(160,215,255,0.85))'
                                : isDark
                                ? 'linear-gradient(180deg, rgba(18,44,72,0.45), rgba(6,16,28,0.65))'
                                : 'linear-gradient(180deg, rgba(235,247,255,0.95), rgba(214,238,255,0.75))',
                            backdropFilter: 'blur(14px)',
                            boxShadow: isDark
                                ? '0 0 20px rgba(0,180,255,0.08), inset 0 0 18px rgba(120,220,255,0.03), inset 0 1px 0 rgba(255,255,255,0.05)'
                                : '0 0 14px rgba(0,140,255,0.10), inset 0 0 14px rgba(0,140,255,0.04), inset 0 1px 0 rgba(255,255,255,0.6)',
                            transition: 'all 0.25s ease',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                border: isDark ? '1px solid rgba(140,240,255,0.35)' : '1px solid rgba(0,140,255,0.40)',
                                boxShadow: isDark
                                    ? '0 0 30px rgba(0,200,255,0.16), inset 0 0 25px rgba(120,220,255,0.06)'
                                    : '0 0 20px rgba(0,150,255,0.18), inset 0 0 18px rgba(0,150,255,0.07)'
                            }
                        }}
                    >
                        {selectionMode && (
                            <Checkbox
                                checked={!!isSelected}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => onToggleSelect?.(conversation.id)}
                                sx={{ p: 0.5, flexShrink: 0 }}
                            />
                        )}

                        <Avatar
                            src={conversation.avatarUrl || undefined}
                            onError={(e) => {
                                e.target.src = '';
                            }}
                            {...(!conversation.avatarUrl ? stringAvatar(conversation.displayName) : {})}
                            sx={{
                                width: 42,
                                height: 42,
                                boxShadow: isDark ? '0 0 10px rgba(0,180,255,0.3)' : '0 0 8px rgba(0,140,255,0.28)'
                            }}
                        />

                        <Box flex={1} minWidth={0}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Badge
                                    variant="dot"
                                    invisible={!naoLida}
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            background: isDark ? 'rgba(120,240,255,0.9)' : 'rgba(0,140,255,0.85)',
                                            color: isDark ? '#001018' : '#fff',
                                            boxShadow: isDark ? '0 0 10px rgba(120,240,255,0.8)' : '0 0 8px rgba(0,140,255,0.5)'
                                        }
                                    }}
                                >
                                    <Typography
                                        noWrap
                                        sx={{
                                            fontSize: 15,
                                            fontWeight: 500,
                                            color: isDark ? 'rgba(255,255,255,0.92)' : 'rgba(8,35,60,0.90)',
                                            minWidth: 0,
                                            maxWidth: '100%'
                                        }}
                                    >
                                        {truncarNomeContato(conversation.displayName)}
                                    </Typography>
                                </Badge>

                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(8,35,60,0.45)',
                                        whiteSpace: 'nowrap',
                                        ml: 1
                                    }}
                                >
                                    {formatConversationTimestamp(conversation.last_message_time) || 'Sem'}
                                </Typography>
                            </Box>

                            {showState && STATE_LABELS[conversation.state] && (
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-block',
                                        fontSize: 10,
                                        fontWeight: 600,
                                        mt: 0.3,
                                        px: 0.8,
                                        py: 0.1,
                                        borderRadius: '8px',
                                        color: isDark ? 'rgba(120,220,255,0.9)' : 'rgba(0,120,220,0.9)',
                                        background: isDark ? 'rgba(120,220,255,0.12)' : 'rgba(0,120,220,0.10)'
                                    }}
                                >
                                    {STATE_LABELS[conversation.state]}
                                </Box>
                            )}

                            <Box display="flex" alignItems="center" gap={0.6} mt={0.5}>
                                {conversation.type_author_last_message === 'user' && (
                                    <IconArrowLeft size={14} color={isDark ? '#7ee7ff' : '#0091ea'} style={{ flexShrink: 0 }} />
                                )}
                                {conversation.type_author_last_message === 'agent' && (
                                    <IconArrowRight size={14} color={isDark ? '#7ee7ff' : '#0091ea'} style={{ flexShrink: 0 }} />
                                )}

                                <Typography
                                    noWrap
                                    sx={{
                                        fontSize: 13,
                                        color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(8,35,60,0.65)',
                                        minWidth: 0,
                                        width: '100%'
                                    }}
                                >
                                    {truncarUltimaMensagem(conversation.last_message) || 'Sem última mensagem'}
                                </Typography>
                            </Box>
                        </Box>

                        {channel[conversation.type] && (
                            <Box display="flex" flexDirection="column" alignItems="center" sx={{ flexShrink: 0 }}>
                                <Avatar
                                    sx={{
                                        width: 26,
                                        height: 26,
                                        border: isDark ? '1px solid rgba(120,220,255,0.2)' : '1px solid rgba(0,120,220,0.25)',
                                        background: isDark ? 'rgba(20,40,60,0.6)' : 'rgba(225,242,255,0.8)'
                                    }}
                                    src={channel[conversation.type].icon}
                                />
                                <Typography
                                    sx={{
                                        mt: 0.4,
                                        fontSize: 11,
                                        whiteSpace: 'nowrap',
                                        color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(8,35,60,0.45)'
                                    }}
                                >
                                    {channel[conversation.type].label}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};

export default ConversasGeral;
